import { useTranslation } from "react-i18next";
import View from "components/elements/View";
import {
  useActivateListMutation,
  useActivateOutgoingCallsMutation,
  useGetPackageActivationDataQuery,
} from "store/activateReserves";
import { Button, Checkbox, Col, notification, Row, Table } from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  ActivateListParams,
  TableDataType,
  PackageNumberType,
  CheckboxType,
} from "types/activateReserves.types";
import { ColumnsType } from "antd/lib/table/interface";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import sortNumber from "helpers/sortNumber";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";
import styles from "./ActivateReserves.module.less";

function ActivateReserves(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, refetch } =
    useGetPackageActivationDataQuery();
  const [activateList, activateListResult] = useActivateListMutation();
  const [activateOutgoingCalls, activateOutgoingCallsResult] =
    useActivateOutgoingCallsMutation();

  const [tableData, setTableData] = useState<TableDataType[] | null>(null);
  const [selectedData, setSelectedData] = useState<ActivateListParams>({});
  const [
    activationConfirmationModalVisible,
    setActivateConfirmationModalVisible,
  ] = useState(false);
  const [
    activateOutgoingCallsConfirmationModalVisible,
    setSctivateOutgoingCallsConfirmationModalVisible,
  ] = useState(false);

  const addToExistingQuotaCell = (quotaCell: string, activePackage: number) => {
    const activePackages = [...selectedData[quotaCell], activePackage];
    const sorted = activePackages.sort((a: number, b: number) =>
      sortNumber(a, b)
    );
    setSelectedData({ ...selectedData, [quotaCell]: sorted });
  };

  const removeFromExistingQuotaCell = (
    quotaCell: string,
    activePackage: number
  ) => {
    const activePackages = selectedData[quotaCell].filter(
      (item) => item !== activePackage
    );

    if (activePackages.length > 0) {
      setSelectedData({ ...selectedData, [quotaCell]: [...activePackages] });
    } else {
      setSelectedData((current) => {
        const copy = { ...current };
        delete copy[quotaCell];
        return copy;
      });
    }
  };

  const updateExistingQuotaCell = (
    quotaCell: string,
    activePackage: number,
    isChecked: boolean
  ) => {
    if (isChecked) {
      addToExistingQuotaCell(quotaCell, activePackage);
    } else {
      removeFromExistingQuotaCell(quotaCell, activePackage);
    }
  };

  const prepareSelectedData = (
    quotaCell: string,
    isChecked: boolean,
    activePackage: number
  ) => {
    const isQuotaCellSelected = Object.keys(selectedData).includes(quotaCell);
    if (isQuotaCellSelected) {
      updateExistingQuotaCell(quotaCell, activePackage, isChecked);
    } else {
      setSelectedData({ ...selectedData, [quotaCell]: [activePackage] });
    }
  };

  const handleOnCheckboxChange = (
    e: CheckboxChangeEvent,
    stringNumber: string,
    quotaCell: string,
    shift: string,
    amountPerPackage: number
  ) => {
    const activePackage = Number(stringNumber);
    const isChecked = e.target.checked;
    prepareSelectedData(quotaCell, isChecked, activePackage);

    if (tableData) {
      const currentIndex = tableData.findIndex((row) => row.shift === shift);
      const updatedRow = { ...tableData[currentIndex] };
      updatedRow.affectedContacts = isChecked
        ? (updatedRow.affectedContacts as number) + amountPerPackage
        : (updatedRow.affectedContacts as number) - amountPerPackage;
      updatedRow.totalContacts = isChecked
        ? (updatedRow.totalContacts as number) + amountPerPackage
        : (updatedRow.totalContacts as number) - amountPerPackage;
      (updatedRow[activePackage] as CheckboxType).value = isChecked;
      const newTableData = tableData.slice();
      newTableData[currentIndex] = updatedRow;
      setTableData(newTableData);
    }
  };

  const prepareCheckboxes = (key: string) => {
    const activePackages = data?.initialData?.[key]?.currentActivePackages;
    const packagesData: PackageNumberType = {};
    const quotaCell = data?.initialData?.[key].quotenzelle || "";
    const shift = data?.initialData?.[quotaCell].schicht || "";
    const amountPerPackage = data?.initialData?.[quotaCell].amountPerPackage;

    if (activePackages) {
      data?.packageNrs.forEach((nr) => {
        const preparedKey = nr.toString();
        packagesData[preparedKey] = {
          initialValue: activePackages.includes(nr),
          value: activePackages.includes(nr),
          quotaCell,
          shift,
          amountPerPackage: amountPerPackage?.[nr] || 0,
        };
      });
    }
    return packagesData;
  };

  const calculateTotalContact = (key: string): number => {
    const activePackages = data?.initialData?.[key].currentActivePackages;
    const amountPerPackage = data?.initialData?.[key].amountPerPackage;
    let sum = 0;
    if (activePackages && amountPerPackage) {
      activePackages.forEach((packageNr) => {
        const preparedKey = packageNr.toString();
        sum += amountPerPackage[preparedKey];
      });
    }
    return sum;
  };

  const preparePackageNrColumns = () => {
    const columns: ColumnsType<TableDataType> = [];
    data?.packageNrs.forEach((item) => {
      const stringNumber = item.toString();

      columns.push({
        title: stringNumber,
        dataIndex: stringNumber,
        key: stringNumber,
        className: `${styles.center}`,
        render: (checkbox: CheckboxType) => {
          const { amountPerPackage, shift, quotaCell, initialValue, value } =
            checkbox;
          const disabled = initialValue || amountPerPackage === 0;
          return (
            <Checkbox
              defaultChecked={initialValue}
              checked={value}
              disabled={disabled}
              onChange={(e: CheckboxChangeEvent) =>
                handleOnCheckboxChange(
                  e,
                  stringNumber,
                  quotaCell,
                  shift,
                  amountPerPackage
                )
              }
            />
          );
        },
      });
    });
    return columns;
  };

  const prepareInitialTableData = () => {
    const tableDataSource: TableDataType[] = [];
    if (data !== undefined) {
      Object.keys(data.initialData).forEach((key) => {
        const packagesData = prepareCheckboxes(key);
        const dataFromApi = {
          shift: data?.initialData[key].schicht,
          affectedContacts: 0,
          totalContacts: calculateTotalContact(key),
        };

        tableDataSource.push(Object.assign(dataFromApi, packagesData));
      });
    }

    setTableData(tableDataSource);
  };

  const handleActivate = () => {
    setActivateConfirmationModalVisible(true);
  };

  const handleActivateOutgoingCalls = () => {
    setSctivateOutgoingCallsConfirmationModalVisible(true);
  };

  const reset = () => {
    prepareInitialTableData();
  };

  const handleActivateConfirm = async () => {
    try {
      const payload = await activateList(selectedData).unwrap();

      if (!payload.hasErrors) {
        notification.success({
          message: t("activate_reserves.message.success"),
        });
        refetch();
      } else {
        reset();
        notification.error({ message: payload.errorDetails.errorMessage });
      }
    } catch (error) {
      reset();
      console.error("Error", error);
    } finally {
      setSelectedData({});
      setActivateConfirmationModalVisible(false);
    }
  };

  const handleActivateOutgoingCallsConfirm = async () => {
    try {
      const payload = await activateOutgoingCalls(undefined).unwrap();

      if (!payload.hasErrors) {
        notification.success({
          message: t("activate_reserves.message.success"),
        });
      } else {
        notification.error({ message: payload.errorDetails.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setSctivateOutgoingCallsConfirmationModalVisible(false);
    }
  };

  const allSumsForAffected =
    tableData?.map((row) => row.affectedContacts as number) || [];
  const sumAffectedContacts = allSumsForAffected.reduce((a, b) => a + b, 0);
  const allSumsForTotal =
    tableData?.map((row) => row.totalContacts as number) || [];
  const sumTotalContacts = allSumsForTotal.reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (data) {
      prepareInitialTableData();
    }
  }, [data]);

  const packageColumns = useMemo(() => preparePackageNrColumns(), [tableData]);
  const columns = [
    {
      title: t("activate_reserves.table.labels.shift"),
      dataIndex: "shift",
      key: "shift",
      width: 170,
    },
    {
      title: t("activate_reserves.table.labels.package_nr"),
      key: "packageNr",
      children: [...packageColumns],
    },
    {
      title: t("activate_reserves.table.labels.affected_contacts"),
      dataIndex: "affectedContacts",
      key: "affectedContacts",
      className: `${styles.right}`,
      width: 170,
    },
    {
      title: t("activate_reserves.table.labels.total_contacts"),
      dataIndex: "totalContacts",
      key: "totalContacts",
      className: `${styles.right}`,
      width: 170,
    },
  ];

  return (
    <View
      title={t("activate_reserves.title")}
      isLoading={isLoading || isFetching}
    >
      {tableData ? (
        <>
          <Table
            bordered
            dataSource={tableData}
            rowKey={(item) => item.shift}
            columns={columns}
            pagination={false}
            size="small"
            className={styles.table}
          />
          <Row justify="end">
            <Col>
              <div className={styles.total}>{sumAffectedContacts}</div>
            </Col>
            <Col>
              <div className={styles.total}>{sumTotalContacts}</div>
            </Col>
          </Row>
          <Row justify="end" gutter={20}>
            <Col>
              <Button
                type="primary"
                onClick={handleActivate}
                disabled={Object.keys(selectedData).length === 0}
                className={styles.button}
              >
                {t("activate_reserves.activate_contacts")}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={handleActivateOutgoingCalls}
                className={styles.button}
              >
                {t("activate_reserves.activate_outgoing_calls")}
              </Button>
            </Col>
          </Row>
        </>
      ) : null}
      <ConfirmationModal
        visible={activationConfirmationModalVisible}
        isLoading={activateListResult.isLoading}
        title={t("activate_reserves.modalActivateAndAkb.title")}
        description={t("activate_reserves.modalActivateAndAkb.description")}
        onClose={() => setActivateConfirmationModalVisible(false)}
        onConfirm={() => handleActivateConfirm()}
      />
      <ConfirmationModal
        visible={activateOutgoingCallsConfirmationModalVisible}
        isLoading={activateOutgoingCallsResult.isLoading}
        title={t("activate_reserves.modalOutgoingCalls.title")}
        description={t("activate_reserves.modalOutgoingCalls.description")}
        onClose={() => setSctivateOutgoingCallsConfirmationModalVisible(false)}
        onConfirm={() => handleActivateOutgoingCallsConfirm()}
      />
    </View>
  );
}

export default ActivateReserves;
